import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi } from '@/lib/api';

export function PricingHistoryPage() {
  const [model, setModel] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['pricing-history', model],
    queryFn: () => adminApi.listPricingHistory({ model: model.trim() || undefined, limit: 200 }),
  });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold tracking-tight">改价历史</h2>
      <p className="text-sm text-muted-foreground">
        每次 model_pricing 表 INSERT / UPDATE / DELETE 自动写一行历史快照。回看"X 月 X 日，价格是多少"。
      </p>

      <Card>
        <CardContent className="flex gap-3 pt-6">
          <Field label="按 model 过滤（留空看全部）">
            <Input
              placeholder="moonshotai/Kimi-K2.5"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
      {data && (
        <>
          <p className="text-xs text-muted-foreground">共 {data.length} 条（最多 200）</p>
          <Table>
            <THead>
              <TR>
                <TH className="w-44">时间</TH>
                <TH>变更</TH>
                <TH>model</TH>
                <TH>USD in_unc / in_c / out (per 1M)</TH>
                <TH>active</TH>
                <TH>by</TH>
              </TR>
            </THead>
            <TBody>
              {data.map((r) => (
                <TR key={r.id}>
                  <TD className="text-xs whitespace-nowrap">
                    {new Date(r.changed_at).toLocaleString('zh-CN')}
                  </TD>
                  <TD>
                    <KindBadge kind={r.change_kind} />
                  </TD>
                  <TD className="font-mono text-xs">{r.model}</TD>
                  <TD className="font-mono text-xs">
                    {fmt(r.cost_usd_per_1m_input_uncached)} / {fmt(r.cost_usd_per_1m_input_cached)} /{' '}
                    {fmt(r.cost_usd_per_1m_output)}
                  </TD>
                  <TD className="text-xs">{r.is_active === null ? '—' : r.is_active ? '✓' : '✗'}</TD>
                  <TD className="text-xs text-muted-foreground">{r.changed_by ?? '—'}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </>
      )}
    </div>
  );
}

function KindBadge({ kind }: { kind: 'insert' | 'update' | 'delete' }) {
  const map = {
    insert: 'bg-blue-100 text-blue-800',
    update: 'bg-amber-100 text-amber-800',
    delete: 'bg-red-100 text-red-800',
  };
  return <span className={`text-xs rounded px-2 py-0.5 ${map[kind]}`}>{kind}</span>;
}

function fmt(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${v}`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
